import React, { useEffect, useRef, useState } from "react"
import useApi from "../../hooks/useApi.js"

const SCOPE_OPTIONS = [ "conservative", "moderate", "aggressive" ]
const APPROVAL_OPTIONS = [ "manual", "first_then_auto", "auto" ]
const FLICKER_OPTIONS = [ "hide", "fade" ]
const SELECTOR_SUGGESTIONS = [ ".checkout-form", ".cart", ".payment", ".price" ]
const PATH_SUGGESTIONS = [ "/checkout*", "/cart", "/account/*", "/thank-you" ]
const CONVERSION_RULE_TYPES = [ "url_match", "css_selector", "custom_event" ]

function SectionHeader({ t, eyebrowKey, titleKey, ledeKey }) {
  return (
    <header className="space-y-2">
      <p
        className="font-mono text-[10px] text-ink-tertiary"
        style={{ letterSpacing: "0.15em", fontVariantNumeric: "tabular-nums" }}
      >
        {t(`dashboard.settings.${eyebrowKey}`)}
      </p>
      <h2
        className="text-[22px] font-medium text-ink-primary"
        style={{ letterSpacing: "-0.03em" }}
      >
        {t(`dashboard.settings.${titleKey}`)}
      </h2>
      {ledeKey && (
        <p className="text-sm text-ink-secondary max-w-2xl" style={{ lineHeight: 1.55 }}>
          {t(`dashboard.settings.${ledeKey}`)}
        </p>
      )}
    </header>
  )
}

function RadioCard({ active, disabled, onSelect, label, desc }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      className="text-left rounded-card border bg-surface-card p-5 disabled:opacity-60 transition-colors"
      style={{
        borderColor: active ? "var(--color-ai)" : "var(--color-rule)",
        boxShadow: active ? "inset 0 0 0 1px var(--color-ai)" : "none"
      }}
    >
      <div className="flex items-center gap-2">
        <span
          aria-hidden
          className="inline-block w-[10px] h-[10px] rounded-full border"
          style={{
            borderColor: active ? "var(--color-ai)" : "var(--color-ink-faint)",
            background: active ? "var(--color-ai)" : "transparent"
          }}
        />
        <span className="text-[15px] font-medium text-ink-primary">{label}</span>
      </div>
      <p className="mt-2 text-[13px] text-ink-secondary" style={{ lineHeight: 1.55 }}>
        {desc}
      </p>
    </button>
  )
}

function SaveIndicator({ t, saving, savedAt, error }) {
  if (error) return <span className="font-mono text-[11px] text-failure">{error}</span>
  if (saving) return <span className="font-mono text-[11px] text-ink-tertiary">{t("dashboard.settings.saving")}</span>
  if (savedAt) return <span className="font-mono text-[11px] text-winner">{t("dashboard.settings.saved")}</span>
  return null
}

function ProtectedSelectors({ t, selectors, onAdd, onRemove, busy }) {
  const [draft, setDraft] = useState("")

  const submit = (e) => {
    e.preventDefault()
    const value = draft.trim()
    if (!value || selectors.includes(value)) return
    onAdd(value)
    setDraft("")
  }

  const suggest = (value) => {
    if (selectors.includes(value)) return
    onAdd(value)
  }

  return (
    <div className="space-y-4">
      {selectors.length === 0 ? (
        <p className="text-[13px] text-ink-tertiary" style={{ lineHeight: 1.55 }}>
          {t("dashboard.settings.selectors_empty")}
        </p>
      ) : (
        <ul className="space-y-2">
          {selectors.map((s) => (
            <li
              key={s}
              className="flex items-center justify-between gap-3 bg-surface-card border border-rule rounded-card px-4 py-3"
            >
              <code
                className="font-mono text-[12px] text-ink-primary"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {s}
              </code>
              <button
                type="button"
                onClick={() => onRemove(s)}
                disabled={busy}
                className="text-[12px] text-ink-tertiary hover:text-failure disabled:opacity-60"
              >
                {t("dashboard.settings.selectors_remove")}
              </button>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={submit} className="flex gap-2 items-stretch">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={busy}
          placeholder={t("dashboard.settings.selectors_placeholder")}
          className="flex-1 bg-surface-card border border-rule rounded-card px-3 py-2 text-[13px] font-mono text-ink-primary disabled:opacity-60"
          style={{ fontVariantNumeric: "tabular-nums" }}
        />
        <button
          type="submit"
          disabled={busy || !draft.trim()}
          className="bg-brand text-ink-on-dark rounded-button px-4 py-2 text-[13px] font-medium disabled:opacity-60"
        >
          {busy ? t("dashboard.settings.selectors_adding") : t("dashboard.settings.selectors_add")}
        </button>
      </form>

      <div className="flex flex-wrap items-center gap-2">
        <span
          className="font-mono text-[10px] text-ink-tertiary"
          style={{ letterSpacing: "0.10em" }}
        >
          {t("dashboard.settings.selectors_suggestions_label")}
        </span>
        {SELECTOR_SUGGESTIONS.map((s) => {
          const already = selectors.includes(s)
          return (
            <button
              key={s}
              type="button"
              onClick={() => suggest(s)}
              disabled={busy || already}
              className="font-mono text-[11px] border border-rule rounded-pill px-2 py-[2px] text-ink-secondary hover:text-ai disabled:opacity-40"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {s}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function ProtectedPaths({ t, paths, onAdd, onRemove, busy }) {
  const [draft, setDraft] = useState("")

  const submit = (e) => {
    e.preventDefault()
    const value = draft.trim()
    if (!value || paths.includes(value)) return
    onAdd(value)
    setDraft("")
  }

  const suggest = (value) => {
    if (paths.includes(value)) return
    onAdd(value)
  }

  return (
    <div className="space-y-4">
      {paths.length === 0 ? (
        <p className="text-[13px] text-ink-tertiary" style={{ lineHeight: 1.55 }}>
          {t("dashboard.settings.paths_empty")}
        </p>
      ) : (
        <ul className="space-y-2">
          {paths.map((p) => (
            <li
              key={p}
              className="flex items-center justify-between gap-3 bg-surface-card border border-rule rounded-card px-4 py-3"
            >
              <code
                className="font-mono text-[12px] text-ink-primary"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {p}
              </code>
              <button
                type="button"
                onClick={() => onRemove(p)}
                disabled={busy}
                className="text-[12px] text-ink-tertiary hover:text-failure disabled:opacity-60"
              >
                {t("dashboard.settings.paths_remove")}
              </button>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={submit} className="flex gap-2 items-stretch">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={busy}
          placeholder={t("dashboard.settings.paths_placeholder")}
          className="flex-1 bg-surface-card border border-rule rounded-card px-3 py-2 text-[13px] font-mono text-ink-primary disabled:opacity-60"
          style={{ fontVariantNumeric: "tabular-nums" }}
        />
        <button
          type="submit"
          disabled={busy || !draft.trim()}
          className="bg-brand text-ink-on-dark rounded-button px-4 py-2 text-[13px] font-medium disabled:opacity-60"
        >
          {busy ? t("dashboard.settings.paths_adding") : t("dashboard.settings.paths_add")}
        </button>
      </form>

      <div className="flex flex-wrap items-center gap-2">
        <span
          className="font-mono text-[10px] text-ink-tertiary"
          style={{ letterSpacing: "0.10em" }}
        >
          {t("dashboard.settings.paths_suggestions_label")}
        </span>
        {PATH_SUGGESTIONS.map((p) => {
          const already = paths.includes(p)
          return (
            <button
              key={p}
              type="button"
              onClick={() => suggest(p)}
              disabled={busy || already}
              className="font-mono text-[11px] border border-rule rounded-pill px-2 py-[2px] text-ink-secondary hover:text-ai disabled:opacity-40"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {p}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function ConversionRules({ t, rules, onAdd, onRemove, busy }) {
  const [ruleType, setRuleType] = useState("url_match")
  const [pattern, setPattern] = useState("")

  const submit = (e) => {
    e.preventDefault()
    const value = pattern.trim()
    if (!value) return
    onAdd({ rule_type: ruleType, pattern: value })
    setPattern("")
  }

  const placeholderByType = {
    url_match: t("dashboard.settings.conversions_pattern_placeholder_url"),
    css_selector: t("dashboard.settings.conversions_pattern_placeholder_css"),
    custom_event: t("dashboard.settings.conversions_pattern_placeholder_event")
  }

  return (
    <div className="space-y-4">
      {rules.length === 0 ? (
        <p className="text-[13px] text-ink-tertiary" style={{ lineHeight: 1.55 }}>
          {t("dashboard.settings.conversions_empty")}
        </p>
      ) : (
        <ul className="space-y-2">
          {rules.map((r) => (
            <li
              key={r.id}
              className="flex items-center justify-between gap-3 bg-surface-card border border-rule rounded-card px-4 py-3"
            >
              <div className="min-w-0">
                <span
                  className="font-mono text-[10px] text-ai"
                  style={{ letterSpacing: "0.10em" }}
                >
                  {t(`dashboard.settings.conversions_type_${r.rule_type}`)}
                </span>
                <code
                  className="block font-mono text-[12px] text-ink-primary mt-1 truncate"
                  style={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {r.pattern}
                </code>
              </div>
              <button
                type="button"
                onClick={() => onRemove(r)}
                disabled={busy}
                className="text-[12px] text-ink-tertiary hover:text-failure disabled:opacity-60 shrink-0"
              >
                {t("dashboard.settings.conversions_remove")}
              </button>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={submit} className="flex flex-wrap gap-2 items-stretch">
        <select
          value={ruleType}
          onChange={(e) => setRuleType(e.target.value)}
          disabled={busy}
          className="bg-surface-card border border-rule rounded-card px-3 py-2 text-[13px] text-ink-primary disabled:opacity-60"
        >
          {CONVERSION_RULE_TYPES.map((rt) => (
            <option key={rt} value={rt}>
              {t(`dashboard.settings.conversions_type_${rt}`)}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={pattern}
          onChange={(e) => setPattern(e.target.value)}
          disabled={busy}
          placeholder={placeholderByType[ruleType]}
          className="flex-1 min-w-[200px] bg-surface-card border border-rule rounded-card px-3 py-2 text-[13px] font-mono text-ink-primary disabled:opacity-60"
          style={{ fontVariantNumeric: "tabular-nums" }}
        />
        <button
          type="submit"
          disabled={busy || !pattern.trim()}
          className="bg-brand text-ink-on-dark rounded-button px-4 py-2 text-[13px] font-medium disabled:opacity-60"
        >
          {busy ? t("dashboard.settings.conversions_adding") : t("dashboard.settings.conversions_add")}
        </button>
      </form>
    </div>
  )
}

export default function SettingsPage({ t, shopState }) {
  const api = useApi()
  const setting = shopState.shop?.setting
  const conversionRules = shopState.shop?.conversion_rules || []

  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState(null)
  const [error, setError] = useState(null)

  // Local optimistic state for protected_selectors + protected_paths so add/remove
  // feels instant. Server response replaces this on success; on error we revert
  // by re-reading shopState.
  const [localSelectors, setLocalSelectors] = useState(setting?.protected_selectors || [])
  const [localPaths, setLocalPaths] = useState(setting?.protected_paths || [])

  useEffect(() => {
    setLocalSelectors(setting?.protected_selectors || [])
  }, [ setting?.protected_selectors ])

  useEffect(() => {
    setLocalPaths(setting?.protected_paths || [])
  }, [ setting?.protected_paths ])

  useEffect(() => {
    if (!savedAt) return
    const id = setTimeout(() => setSavedAt(null), 2000)
    return () => clearTimeout(id)
  }, [ savedAt ])

  const save = async (updates) => {
    setSaving(true)
    setError(null)
    try {
      await api.patch("/api/v1/shop/setting", { setting: updates })
      await shopState.refresh()
      setSavedAt(Date.now())
    } catch (err) {
      setError(err.message || t("dashboard.settings.save_error"))
    } finally {
      setSaving(false)
    }
  }

  const setScope = (value) => {
    if (value === setting?.ai_scope) return
    save({ ai_scope: value })
  }
  const setApproval = (value) => {
    if (value === setting?.ai_approval) return
    save({ ai_approval: value })
  }
  const setFlicker = (value) => {
    if (value === setting?.anti_flicker_mode) return
    save({ anti_flicker_mode: value })
  }
  const addSelector = (value) => {
    const next = [ ...localSelectors, value ]
    setLocalSelectors(next)
    save({ protected_selectors: next })
  }
  const removeSelector = (value) => {
    const next = localSelectors.filter((s) => s !== value)
    setLocalSelectors(next)
    save({ protected_selectors: next })
  }
  // M9/9h — policy signals. Each setter coerces the input to its storage
  // shape (integer or nullable boolean) before PATCH. Empty string + "unset"
  // map to nil so the owner can clear a value they regret.
  const setPolicyInt = (field, raw) => {
    const parsed = raw === "" ? null : parseInt(raw, 10)
    if (Number.isNaN(parsed)) return
    save({ [field]: parsed })
  }
  const setPolicyTriBool = (field, raw) => {
    const next = raw === "" ? null : raw === "true"
    save({ [field]: next })
  }

  const addPath = (value) => {
    const next = [ ...localPaths, value ]
    setLocalPaths(next)
    save({ protected_paths: next })
  }
  const removePath = (value) => {
    const next = localPaths.filter((p) => p !== value)
    setLocalPaths(next)
    save({ protected_paths: next })
  }

  const addConversionRule = async ({ rule_type, pattern }) => {
    setSaving(true)
    setError(null)
    try {
      await api.post("/api/v1/shop/conversion_rules", {
        conversion_rule: { rule_type, pattern }
      })
      await shopState.refresh()
      setSavedAt(Date.now())
    } catch (err) {
      setError(err.message || t("dashboard.settings.save_error"))
    } finally {
      setSaving(false)
    }
  }

  const removeConversionRule = async (rule) => {
    setSaving(true)
    setError(null)
    try {
      await api.destroy(`/api/v1/shop/conversion_rules/${rule.id}`)
      await shopState.refresh()
      setSavedAt(Date.now())
    } catch (err) {
      setError(err.message || t("dashboard.settings.save_error"))
    } finally {
      setSaving(false)
    }
  }

  if (!setting) {
    return <p className="text-sm text-ink-tertiary">…</p>
  }

  return (
    <div className="space-y-12">
      <header className="flex items-baseline justify-between gap-4 flex-wrap">
        <div className="space-y-3">
          <p
            className="font-mono text-[10px] text-ai"
            style={{ letterSpacing: "0.15em", fontVariantNumeric: "tabular-nums" }}
          >
            {t("dashboard.settings.eyebrow")}
          </p>
          <h1
            className="text-[28px] font-medium text-ink-primary"
            style={{ letterSpacing: "-0.035em" }}
          >
            {t("dashboard.settings.title")}
          </h1>
          <p className="text-sm text-ink-secondary max-w-2xl" style={{ lineHeight: 1.55 }}>
            {t("dashboard.settings.lede")}
          </p>
        </div>
        <SaveIndicator t={t} saving={saving} savedAt={savedAt} error={error} />
      </header>

      <section className="space-y-5">
        <SectionHeader
          t={t}
          eyebrowKey="scope_section_eyebrow"
          titleKey="scope_section_title"
          ledeKey="scope_section_lede"
        />
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}
        >
          {SCOPE_OPTIONS.map((opt) => (
            <RadioCard
              key={opt}
              active={setting.ai_scope === opt}
              disabled={saving}
              onSelect={() => setScope(opt)}
              label={t(`dashboard.settings.scope_${opt}_label`)}
              desc={t(`dashboard.settings.scope_${opt}_desc`)}
            />
          ))}
        </div>
      </section>

      <section className="space-y-5">
        <SectionHeader
          t={t}
          eyebrowKey="approval_section_eyebrow"
          titleKey="approval_section_title"
          ledeKey="approval_section_lede"
        />
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}
        >
          {APPROVAL_OPTIONS.map((opt) => (
            <RadioCard
              key={opt}
              active={setting.ai_approval === opt}
              disabled={saving}
              onSelect={() => setApproval(opt)}
              label={t(`dashboard.settings.approval_${opt}_label`)}
              desc={t(`dashboard.settings.approval_${opt}_desc`)}
            />
          ))}
        </div>
      </section>

      <section className="space-y-5">
        <SectionHeader
          t={t}
          eyebrowKey="flicker_section_eyebrow"
          titleKey="flicker_section_title"
        />
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}
        >
          {FLICKER_OPTIONS.map((opt) => (
            <RadioCard
              key={opt}
              active={setting.anti_flicker_mode === opt}
              disabled={saving}
              onSelect={() => setFlicker(opt)}
              label={t(`dashboard.settings.flicker_${opt}_label`)}
              desc={t(`dashboard.settings.flicker_${opt}_desc`)}
            />
          ))}
        </div>
      </section>

      <section className="space-y-5">
        <SectionHeader
          t={t}
          eyebrowKey="conversions_section_eyebrow"
          titleKey="conversions_section_title"
          ledeKey="conversions_section_lede"
        />
        <ConversionRules
          t={t}
          rules={conversionRules}
          onAdd={addConversionRule}
          onRemove={removeConversionRule}
          busy={saving}
        />
      </section>

      <section className="space-y-5">
        <SectionHeader
          t={t}
          eyebrowKey="selectors_section_eyebrow"
          titleKey="selectors_section_title"
          ledeKey="selectors_section_lede"
        />
        <ProtectedSelectors
          t={t}
          selectors={localSelectors}
          onAdd={addSelector}
          onRemove={removeSelector}
          busy={saving}
        />
      </section>

      <section className="space-y-5">
        <SectionHeader
          t={t}
          eyebrowKey="paths_section_eyebrow"
          titleKey="paths_section_title"
          ledeKey="paths_section_lede"
        />
        <ProtectedPaths
          t={t}
          paths={localPaths}
          onAdd={addPath}
          onRemove={removePath}
          busy={saving}
        />
      </section>

      <section className="space-y-5">
        <SectionHeader
          t={t}
          eyebrowKey="policy_section_eyebrow"
          titleKey="policy_section_title"
          ledeKey="policy_section_lede"
        />
        <PolicySignals
          t={t}
          setting={setting}
          onIntChange={setPolicyInt}
          onTriBoolChange={setPolicyTriBool}
          busy={saving}
        />
      </section>
    </div>
  )
}

function PolicyField({ t, label, help, children }) {
  return (
    <div className="bg-surface-card border border-rule rounded-card p-5 space-y-2">
      <label className="block text-[14px] font-medium text-ink-primary">{label}</label>
      <p className="text-[13px] text-ink-secondary" style={{ lineHeight: 1.55 }}>{help}</p>
      <div className="pt-2">{children}</div>
    </div>
  )
}

// M9/9h — 4 policy columns that gate archetypes the playbook library has
// runtime_gates for: review_count_min_5 (social_proof), verifiable_stock_
// or_deadline (urgency_scarcity), free_shipping_threshold_defined
// (free_shipping_bar). nil = not configured; the engine treats unset as
// "don't fire archetypes that depend on this signal" rather than guessing.
// Debounce window for the two integer inputs. Typing "500" at normal
// speed completes well under 400ms; the debounce coalesces the three
// keystrokes into one PATCH instead of three. Tri-state selects stay
// immediate (no multi-keystroke typing on a dropdown).
const POLICY_INT_DEBOUNCE_MS = 400

function PolicySignals({ t, setting, onIntChange, onTriBoolChange, busy }) {
  const triBoolValue = (v) => (v === null || v === undefined ? "" : String(v))
  const intValue = (v) => (v === null || v === undefined ? "" : String(v))

  // Local draft state for the two integer fields. Decouples what the user
  // is typing from the persisted `setting.*` value that flows in from the
  // parent after each save+refresh cycle. Without this, toggling the
  // `disabled` attribute mid-keystroke (busy flips true→false on save)
  // would steal focus and the next digit couldn't be typed.
  const [ reviewDraft, setReviewDraft ] = useState(intValue(setting.review_count))
  const [ shippingDraft, setShippingDraft ] = useState(intValue(setting.free_shipping_threshold))
  const reviewTimerRef = useRef(null)
  const shippingTimerRef = useRef(null)

  // Sync drafts when the persisted value changes externally (e.g. another
  // tab wrote, or a save from blur flushed). Skip when the user is still
  // typing — the pending timer is the source of truth until it fires.
  useEffect(() => {
    if (reviewTimerRef.current) return
    setReviewDraft(intValue(setting.review_count))
  }, [ setting.review_count ])
  useEffect(() => {
    if (shippingTimerRef.current) return
    setShippingDraft(intValue(setting.free_shipping_threshold))
  }, [ setting.free_shipping_threshold ])

  const scheduleIntSave = (field, raw, timerRef) => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      onIntChange(field, raw)
    }, POLICY_INT_DEBOUNCE_MS)
  }

  const flushIntSave = (field, raw, timerRef) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    onIntChange(field, raw)
  }

  return (
    <div
      className="grid gap-4"
      style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}
    >
      <PolicyField
        t={t}
        label={t("dashboard.settings.policy_review_count_label")}
        help={t("dashboard.settings.policy_review_count_help")}
      >
        <input
          type="number"
          min="0"
          step="1"
          value={reviewDraft}
          onChange={(e) => {
            setReviewDraft(e.target.value)
            scheduleIntSave("review_count", e.target.value, reviewTimerRef)
          }}
          onBlur={(e) => flushIntSave("review_count", e.target.value, reviewTimerRef)}
          placeholder={t("dashboard.settings.policy_review_count_placeholder")}
          className="w-full bg-surface-card border border-rule rounded-card px-3 py-2 text-[13px] font-mono text-ink-primary"
          style={{ fontVariantNumeric: "tabular-nums" }}
        />
      </PolicyField>

      <PolicyField
        t={t}
        label={t("dashboard.settings.policy_has_live_stock_label")}
        help={t("dashboard.settings.policy_has_live_stock_help")}
      >
        <select
          value={triBoolValue(setting.has_live_stock)}
          onChange={(e) => onTriBoolChange("has_live_stock", e.target.value)}
          disabled={busy}
          className="w-full bg-surface-card border border-rule rounded-card px-3 py-2 text-[13px] text-ink-primary disabled:opacity-60"
        >
          <option value="">{t("dashboard.settings.policy_tribool_unset")}</option>
          <option value="true">{t("dashboard.settings.policy_tribool_yes")}</option>
          <option value="false">{t("dashboard.settings.policy_tribool_no")}</option>
        </select>
      </PolicyField>

      <PolicyField
        t={t}
        label={t("dashboard.settings.policy_has_verifiable_deadline_label")}
        help={t("dashboard.settings.policy_has_verifiable_deadline_help")}
      >
        <select
          value={triBoolValue(setting.has_verifiable_deadline)}
          onChange={(e) => onTriBoolChange("has_verifiable_deadline", e.target.value)}
          disabled={busy}
          className="w-full bg-surface-card border border-rule rounded-card px-3 py-2 text-[13px] text-ink-primary disabled:opacity-60"
        >
          <option value="">{t("dashboard.settings.policy_tribool_unset")}</option>
          <option value="true">{t("dashboard.settings.policy_tribool_yes")}</option>
          <option value="false">{t("dashboard.settings.policy_tribool_no")}</option>
        </select>
      </PolicyField>

      <PolicyField
        t={t}
        label={t("dashboard.settings.policy_free_shipping_label")}
        help={t("dashboard.settings.policy_free_shipping_help")}
      >
        <input
          type="number"
          min="0"
          step="1"
          value={shippingDraft}
          onChange={(e) => {
            setShippingDraft(e.target.value)
            scheduleIntSave("free_shipping_threshold", e.target.value, shippingTimerRef)
          }}
          onBlur={(e) => flushIntSave("free_shipping_threshold", e.target.value, shippingTimerRef)}
          placeholder={t("dashboard.settings.policy_free_shipping_placeholder")}
          className="w-full bg-surface-card border border-rule rounded-card px-3 py-2 text-[13px] font-mono text-ink-primary"
          style={{ fontVariantNumeric: "tabular-nums" }}
        />
      </PolicyField>
    </div>
  )
}
